import { CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import { useGetChamberDashboardParams } from "src/chamber/data-access/useGetChamberDashboardParams";
import { useGetActiveOrganizationsForAssignee } from "src/supervisoryTeam/data-access/useGetActiveOrganizationsForAssignee";
import { useAuth } from "src/shared/context/useAuth";
import ChangeApplications from "./ui/ChangeApplications";
import CertificateApplications from "src/chamber/features/dashboard/ui/CertificateApplications";
import ESignature from "src/chamber/features/dashboard/ui/ESignature";
import ViolationCases from "src/chamber/features/dashboard/ui/ViolationCases";
import Complaint from "src/chamber/features/dashboard/ui/Complaint";
import SampleInspection from "src/chamber/features/dashboard/ui/SampleInspection";
import SupervisoryOrganizations from "src/chamber/features/dashboard/ui/SupervisoryOrganizations";
import SupervisoryAssigneeCard from "src/chamber/features/dashboard/ui/SupervisoryAssigneeCard";
import SupervisoryAssignerCard from "src/chamber/features/dashboard/ui/SupervisoryAssignerCard";
import {
  useActiveOwnApplicationsCount,
  useActiveOwnOnlyApplicationsCount,
  useOwnRepresentativesApplicationCount,
  useRepresentativesApplicationCount,
  useActiveChamberApplicationsPaidCount,
  useActiveChamberApplicationsUnpaidCount,
  useActiveOwnCertificateApplicationsCount,
  useActiveChamberCertificateApplicationsPaidCount,
  useActiveChamberCertificateApplicationsUnpaidCount,
  useUnsignedOwnCertificateApplicationsCount,
  useActiveOwnViolationCasesCount,
  useUnassignedViolationCasesCount,
  useActiveOwnComplaintsCount,
  useUnassignedComplaintsCount,
  useActiveChamberSampleInspectionsCount,
  useActiveOwnSampleCasesCount,
} from "src/chamber/data-access/counts/useChamberCounts";

function NewDashboard() {
  const {
    userInfo,
    isSupervisorAssignee,
    isSupervisorAssigner,
    isChamber,
    isSampleInitiator,
  } = useAuth();
  const { data, isLoading } = useGetChamberDashboardParams(isChamber);
  const { data: supervisoryOrganizationsData } =
    useGetActiveOrganizationsForAssignee(isSupervisorAssignee);

  // Μετρητές dashboard — lazy/cached, fetch μόνο για χρήστες ΥΓΕΜΗ.
  const sampleActive =
    import.meta.env.VITE_SAMPLE_INSPECTION_ACTIVATED === "true";
  const activeOwnApplicationsQ = useActiveOwnApplicationsCount(isChamber);
  const activeOwnOnlyApplicationsQ =
    useActiveOwnOnlyApplicationsCount(isChamber);
  const ownRepresentativesQ = useOwnRepresentativesApplicationCount(isChamber);
  const allRepresentativesQ = useRepresentativesApplicationCount(isChamber);
  const activeChamberApplicationsPaidQ =
    useActiveChamberApplicationsPaidCount(isChamber);
  const activeChamberApplicationsUnpaidQ =
    useActiveChamberApplicationsUnpaidCount(isChamber);
  const activeOwnCertificateApplicationsQ =
    useActiveOwnCertificateApplicationsCount(isChamber);
  const activeChamberCertificateApplicationsPaidQ =
    useActiveChamberCertificateApplicationsPaidCount(isChamber);
  const activeChamberCertificateApplicationsUnpaidQ =
    useActiveChamberCertificateApplicationsUnpaidCount(isChamber);
  const unsignedOwnCertificateApplicationsQ =
    useUnsignedOwnCertificateApplicationsCount(isChamber);
  const activeOwnViolationCasesQ = useActiveOwnViolationCasesCount(isChamber);
  const unassignedViolationCasesQ = useUnassignedViolationCasesCount(isChamber);
  const activeOwnComplaintsQ = useActiveOwnComplaintsCount(isChamber);
  const unassignedComplaintsQ = useUnassignedComplaintsCount(isChamber);
  const activeChamberSampleInspectionsQ =
    useActiveChamberSampleInspectionsCount(isChamber && sampleActive);
  const activeOwnSampleCasesQ =
    useActiveOwnSampleCasesCount(isChamber && sampleActive);

  // Loading ανά κάρτα: όσο φορτώνει κάποιο count της κάρτας -> spinner στα chips της.
  const changeAppsLoading = [
    activeOwnOnlyApplicationsQ,
    activeOwnApplicationsQ,
    activeChamberApplicationsPaidQ,
    activeChamberApplicationsUnpaidQ,
  ].some((q) => q.isLoading);
  const certAppsLoading = [
    activeOwnCertificateApplicationsQ,
    activeChamberCertificateApplicationsPaidQ,
    activeChamberCertificateApplicationsUnpaidQ,
    unsignedOwnCertificateApplicationsQ,
  ].some((q) => q.isLoading);
  const eSignatureLoading = [ownRepresentativesQ, allRepresentativesQ].some(
    (q) => q.isLoading,
  );
  const violationLoading = [
    activeOwnViolationCasesQ,
    unassignedViolationCasesQ,
  ].some((q) => q.isLoading);
  const complaintLoading = [activeOwnComplaintsQ, unassignedComplaintsQ].some(
    (q) => q.isLoading,
  );
  const sampleLoading = [
    activeChamberSampleInspectionsQ,
    activeOwnSampleCasesQ,
  ].some((q) => q.isLoading);

  localStorage.removeItem("fromUrl");

  if (isSupervisorAssigner && !isChamber) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "80vw",
          mx: "auto",
          gap: 2,
          p: 2,
        }}
      >
        <SupervisoryAssignerCard />
      </Box>
    );
  }

  if (isSupervisorAssignee && !isChamber) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "80vw",
          mx: "auto",
          gap: 2,
          p: 2,
        }}
      >
        {(supervisoryOrganizationsData?.data?.length ?? 0) > 0 && (
          <SupervisoryOrganizations
            organizations={supervisoryOrganizationsData?.data}
          />
        )}
        <SupervisoryAssigneeCard />
      </Box>
    );
  }

  return (
    <>
      {!isLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "80vw",
            justifyContent: "center",
            mx: "auto",
            gap: 2,
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr",
                md: "1fr 1fr",
                lg: "1fr 1fr",
                xl: "1fr 1fr",
              },
              gap: 2,
              p: 2,
              width: "100%",
            }}
          >
            {(supervisoryOrganizationsData?.data?.length ?? 0) > 0 && (
              <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" } }}>
                <SupervisoryOrganizations
                  organizations={supervisoryOrganizationsData?.data}
                />
              </Box>
            )}
            <ChangeApplications
              countsLoading={changeAppsLoading}
              activeOwnOnlyApplications={
                activeOwnOnlyApplicationsQ?.data?.data?.count ?? null
              }
              activeOwnApplications={activeOwnApplicationsQ?.data?.data?.count ?? null}
              activeOwnCertificateApplicationsCount={
                activeOwnCertificateApplicationsQ?.data?.data?.count ?? null
              }
              isOrgAdmin={userInfo.isOrgAdmin}
              activeChamberApplicationsPaidCount={
                activeChamberApplicationsPaidQ?.data?.data?.count ?? null
              }
              activeChamberApplicationsUnpaidCount={
                activeChamberApplicationsUnpaidQ?.data?.data?.count ?? null
              }
              statuses={
                data?.data?.status?.map((status: any) => ({
                  label: status.statusDescr,
                  value: status.statusId,
                })) ?? []
              }
              legalTypes={data?.data?.legalTypes}
              appTypes={data?.data?.appTypes?.map((app: any) => ({
                label: app.m00_descr,
                value: app.m00_id,
              }))}
              chamberUsers={data?.data?.chamberUsers?.map((user: any) => ({
                label: user.a06_fullname,
                value: user.a06_id,
              }))}
              responsibleNow={data?.data?.responsibleNow?.map((resp: any) => ({
                label: resp.descr,
                value: resp.m03_resp,
              }))}
              unsignedOwnCertificateApplicationsCount={null}
            />
            <CertificateApplications
              countsLoading={certAppsLoading}
              activeOwnCertificateApplicationsCount={
                activeOwnCertificateApplicationsQ?.data?.data?.count ?? null
              }
              isOrgAdmin={userInfo.isOrgAdmin}
              activeChamberCertificateApplicationsPaidCount={
                activeChamberCertificateApplicationsPaidQ?.data?.data?.count ?? null
              }
              activeChamberCertificateApplicationsUnpaidCount={
                activeChamberCertificateApplicationsUnpaidQ?.data?.data?.count ?? null
              }
              statuses={data?.data?.status?.map((status: any) => ({
                label: status.statusDescr,
                value: status.statusId,
              }))}
              legalTypes={data?.data?.legalTypes}
              appTypes={data?.data?.appTypes?.map((app: any) => ({
                label: app.m00_descr,
                value: app.m00_id,
              }))}
              chamberUsers={data?.data?.chamberUsers?.map((user: any) => ({
                label: user.a06_fullname,
                value: user.a06_id,
              }))}
              responsibleNow={data?.data?.responsibleNow?.map((resp: any) => ({
                label: resp.descr,
                value: resp.m03_resp,
              }))}
              unsignedOwnCertificateApplicationsCount={
                unsignedOwnCertificateApplicationsQ?.data?.data?.count ?? null
              }
            />
            <ESignature
              countsLoading={eSignatureLoading}
              ownCount={ownRepresentativesQ?.data?.data?.count ?? null}
              allCount={allRepresentativesQ?.data?.data?.count ?? null}
            />
            <ViolationCases
              countsLoading={violationLoading}
              isOrgAdmin={userInfo.isOrgAdmin}
              isSampleInitiator={isSampleInitiator}
              violationStatuses={data?.data?.violationStatuses ?? []}
              violationTypes={data?.data?.violationTypes?.map((vt: any) => ({
                label: vt.description,
                value: vt.id,
              }))}
              chamberUsers={data?.data?.chamberUsers?.map((user: any) => ({
                label: user.a06_fullname,
                value: user.a06_id,
                isChamberAdmin: user.isChamberAdmin,
              }))}
              activeViolationCasesApplications={
                data?.data?.activeViolationCasesApplications
              }
              assignChamberAdminDashboard={
                data?.data?.assignChamberAdminDashboard
              }
              activeOwnViolationCasesCount={
                activeOwnViolationCasesQ?.data?.data?.count ?? null
              }
              unassignedViolationCasesCount={
                unassignedViolationCasesQ?.data?.data?.count ?? null
              }
            />
            <Complaint
              countsLoading={complaintLoading}
              isOrgAdmin={userInfo.isOrgAdmin}
              isSampleInitiator={isSampleInitiator}
              complaintStatuses={
                data?.data?.complaintStatuses?.map((vs: any) => ({
                  label: vs.label,
                  value: vs.id,
                })) ?? []
              }
              chamberUsers={data?.data?.chamberUsers?.map((user: any) => ({
                label: user.a06_fullname,
                value: user.a06_id,
                isChamberAdmin: user.isChamberAdmin,
              }))}
              activeComplaintApplications={
                data?.data?.activeComplaintApplications
              }
              pendingComplaintApplications={
                data?.data?.pendingComplaintApplications
              }
              pastComplaintApplications={data?.data?.pastComplaintApplications}
              violationTypes={data?.data?.violationTypes?.map((vt: any) => ({
                label: vt.description,
                value: vt.id,
              }))}
              activeOwnComplaintsCount={activeOwnComplaintsQ?.data?.data?.count ?? null}
              unassignedComplaintsCount={
                unassignedComplaintsQ?.data?.data?.count ?? null
              }
            />
            {import.meta.env.VITE_SAMPLE_INSPECTION_ACTIVATED === "true" && (
              <SampleInspection
                countsLoading={sampleLoading}
                isOrgAdmin={userInfo.isOrgAdmin}
                isSampleInitiator={isSampleInitiator}
                activeChamberSampleInspectionsCount={
                  activeChamberSampleInspectionsQ?.data?.data?.count ?? null
                }
                activeOwnSampleCasesCount={
                  activeOwnSampleCasesQ?.data?.data?.count ?? null
                }
              />
            )}
            {isSupervisorAssignee && <SupervisoryAssigneeCard />}
            {isSupervisorAssigner && <SupervisoryAssignerCard />}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
}

export default NewDashboard;
